// LedgerPage — financial-only. The downstream record of what Dawn's already
// cleared: approved/exported/synced receipts across every buyer, one calendar
// month at a time, grouped by category with subtotals + a grand total.
// Reconcile is where matching happens; this page is just the ledger of it.

import React, { useEffect, useMemo, useState } from 'react';
import BackLink from '../components/BackLink';
import PageLayout from '../components/ui/PageLayout';
import { IconButton } from '../components/ui/Button';
import { Panel, Stat, Readout } from '../components/ui/Panel';
import { ListRow, ListStack } from '../components/ui/ListRow';
import { EmptyState } from '../components/ui/EmptyState';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import FadeIn from '../components/ui/FadeIn';
import { Tooltip } from '../components/ui/Tooltip';
import { IconChevronLeft, IconChevronRight } from '../components/ui/icons';
import { apiFetchAuthed } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import StatusPill from './StatusPill';
import type { Receipt } from './types';

const money = (v: unknown) => (v == null || v === '' ? '—' : `$${Number(v).toFixed(2)}`);

// 'YYYY-MM' month helpers (lexicographic compare is correct for this format).
// Mirrors ReceiptsPage's month-nav idiom.
const monthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const CURRENT_MONTH = monthKey(new Date());
const monthLabel = (key: string) => {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
};
const shiftMonth = (key: string, delta: number) => {
  const [y, m] = key.split('-').map(Number);
  return monthKey(new Date(y, m - 1 + delta, 1));
};

// Deliberately NOT the store's SUBMITTED_STATUSES (which includes 'submitted'
// — not yet Dawn-cleared). Fetched directly for that reason.
const LEDGER_STATUSES = 'approved,exported,synced';

export default function LedgerPage() {
  const toast = useToast();
  const [month, setMonth] = useState(CURRENT_MONTH);
  const [rows, setRows] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    apiFetchAuthed(`/receipts?status=${LEDGER_STATUSES}&month=${month}&scope=all`)
      .then((data: Receipt[]) => { if (live) setRows(data); })
      .catch((e: any) => toast.error(e?.message || 'Could not load the ledger'))
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const groups = useMemo(() => {
    const m = new Map<string, Receipt[]>();
    rows.forEach((r) => {
      const key = r.category_label || 'Uncategorized';
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(r);
    });
    return Array.from(m.entries()).sort((a, b) => b[1].length - a[1].length);
  }, [rows]);

  const grandTotal = useMemo(() => rows.reduce((sum, r) => sum + (Number(r.total) || 0), 0), [rows]);

  return (
    <PageLayout width="narrow" kind="workspace" className="pt-4 lg:pt-6" header={(
      <>
      <BackLink to="/receipts" label="Receipts" className="mb-3" />

      <div className="flex items-center justify-between mb-6">
        <h1 className="flex items-center gap-2.5 text-2xl font-bold text-white">
          <span className="w-1 h-6 rounded-full bg-primary/60" />
          Ledger
        </h1>
        <div className="flex items-center gap-1">
          <Tooltip label="Previous month">
            <IconButton size="sm" label="Previous month" onClick={() => setMonth((m) => shiftMonth(m, -1))}>
              <IconChevronLeft />
            </IconButton>
          </Tooltip>
          <span className="text-xs font-medium text-on-surface min-w-[7.5rem] text-center">{monthLabel(month)}</span>
          <Tooltip label="Next month">
            <IconButton size="sm" label="Next month" onClick={() => setMonth((m) => shiftMonth(m, 1))} disabled={month >= CURRENT_MONTH}>
              <IconChevronRight />
            </IconButton>
          </Tooltip>
        </div>
      </div>
      </>
    )}>

      <FadeIn key={loading && rows.length === 0 ? 'loading' : rows.length === 0 ? 'empty' : 'rows'}>
      {loading && rows.length === 0 ? (
        <SkeletonGroup label="Loading the ledger" className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14 rounded-panel" />)}</SkeletonGroup>
      ) : rows.length === 0 ? (
        <EmptyState title={`Nothing cleared in ${monthLabel(month)}`}>
          Approved, exported, and synced receipts land here.
        </EmptyState>
      ) : (
        <div className="space-y-6">
          {groups.map(([category, receipts]) => {
            const subtotal = receipts.reduce((s, r) => s + (Number(r.total) || 0), 0);
            return (
              <Panel
                key={category}
                title={category}
                actions={<span className="text-xs font-mono tabular-nums text-on-surface-variant">{money(subtotal)}</span>}
              >
                <ListStack
                  on="panel"
                  label={category}
                  items={receipts}
                  getKey={(r) => String(r.id)}
                  renderRow={(r) => (
                    <ListRow
                      to={`/receipts/${r.public_id}`}
                      title={(
                        <>
                          {r.vendor_name || 'Unknown vendor'}
                          <span className="text-xs font-normal text-on-surface-variant"> · {r.owner_name || r.uploaded_by_name || 'Unknown'}</span>
                          {r.event_name && <span className="text-xs font-normal text-on-surface-variant"> · {r.event_name}</span>}
                        </>
                      )}
                      meta={r.receipt_date || '—'}
                      trailing={(
                        <>
                          <span className="text-sm font-mono tabular-nums text-on-surface">{money(r.total)}</span>
                          <StatusPill status={r.status} />
                        </>
                      )}
                    />
                  )}
                />
              </Panel>
            );
          })}
          <Readout label="Total">
            <Stat size="sm" label="Total" value={<span className="font-mono">{money(grandTotal)}</span>} />
          </Readout>
        </div>
      )}
      </FadeIn>
    </PageLayout>
  );
}
